import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { SearchOutlined } from "@ant-design/icons";
import classNames from "classnames";

import { Input } from "components/form_components/Inputs";
import { ExportNoticesButton } from "components/notices/ExportNoticesButton";
import { FilterForm, FilterFormLocation, groupedKeys } from "components/notices/FilterForm";
import { PaywallBanner } from "components/paywall/PaywallBanner";
import Button from "components/ui/button/Button";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { createUseDebounce } from "lib/debounce";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { SortState } from "lib/search/types";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { ColumnSetting } from "lib/types/models";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import { findKeybyValue } from "../../lib/utils";
import FilterDrawer from "../filter_form/filter_drawer/FilterDrawer";
import { NoticeTable } from "./NoticeTable";
import { QuickFilterBar } from "./QuickFilterBar";
import { useFilterableNoticeTableState } from "./useFilterableNoticeTableState";
import { ALL_NOTICE_COLUMNS, FilterSection, NoticeColumns, NoticeFilters } from "./utils";

import css from "./FilterableNoticeTable.module.scss";

type FormValues = {
  text: string;
};

export function FilterableNoticeTable({
  defaultFilters,
  defaultColumns,
  hiddenSections,
  hiddenFilters,
  paywallEnabled,
  location,
  simple = false,
}: {
  defaultFilters: NoticeFilters;
  defaultColumns: NoticeColumns[];
  hiddenSections?: FilterSection[];
  hiddenFilters?: (keyof NoticeFilters)[];
  paywallEnabled?: boolean;
  location?: FilterFormLocation;
  /**
   * Search, columns and export only: no filter button, panel or applied-filters bar. For a page
   * whose own controls decide which notices these are.
   */
  simple?: boolean;
}) {
  const { noticeTableFilters, setNoticeTableFilters, setNoticeTableColumns, tableColumns } =
    useFilterableNoticeTableState(defaultFilters);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedNotices, setSelectedNotices] = useState<string[]>();
  const [activeGroups, setActiveGroups] = useState<string[]>([]);

  const { logEvent } = useTracking();

  const currentFilters = useMemo(() => {
    if (!noticeTableFilters) {
      return defaultFilters;
    }
    // Simple mode has no filter controls, so only search and sort come from the URL. Any other
    // filter there would narrow the notices with no visible way to clear it.
    if (simple) {
      return { ...defaultFilters, text: noticeTableFilters.text, sort: noticeTableFilters.sort };
    }
    return noticeTableFilters;
  }, [noticeTableFilters, defaultFilters, simple]);

  /**
   * We need to override applied legacy filters
   * Only set supplierMentionType if supplierGuids are set
   */
  const appliedFilters = useMemo(() => {
    currentFilters.supplierMentionType =
      currentFilters.supplierGuids && currentFilters.supplierGuids.length > 0
        ? currentFilters.supplierMentionType
        : undefined;
    // clear signals filter as this is removed & a legacy filter
    currentFilters.signals = [];

    return currentFilters;
  }, [currentFilters]);
  const appliedColumns = tableColumns ? tableColumns : defaultColumns;

  const useDebounce500 = createUseDebounce(500);
  const debouncedSetNoticeTableFilters = useDebounce500(setNoticeTableFilters);
  const debouncedFreeSearchLog = useDebounce500(() => {
    logEvent(EventNames.freeSearch, {
      "Filter name": "Free search",
      "Action type": "Filter applied",
      "Data type": EventDataTypes.record,
    });
  });

  const loggedFilterOpen = () => {
    logEvent(EventNames.filterPanelOpened, {
      "Context source": "Filters button",
      "Data type": "Records",
    });
    setIsFilterDrawerOpen(true);
  };

  const values = appliedFilters;

  const { control, getValues, trigger, watch } = useForm<FormValues>({
    defaultValues: {
      text: appliedFilters.text,
    },
    mode: "onChange",
    values,
  });

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: (data) => {
      debouncedSetNoticeTableFilters({ ...appliedFilters, text: data.text });
    },
  });

  const { authorised: hasAwards } = useCheckSubscription("AWARDS");

  return (
    <>
      {!hasAwards && paywallEnabled ? <PaywallBanner featureType="AWARDS" /> : null}
      <div className={css.filterBar}>
        <div className={css.filterItems}>
          <form
            className={classNames(css.searchInput, { [css.simpleSearchInput]: simple })}
            onSubmit={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <Input
              placeholder={simple ? "Search titles and descriptions" : "Search"}
              prefix={<SearchOutlined />}
              name="text"
              label=""
              control={control}
              allowClear
              onChange={() => debouncedFreeSearchLog()}
            />
          </form>
          {!simple && (
            <Button buttonType="secondary" onClick={loggedFilterOpen} icon="filterFunnel02">
              Filters
            </Button>
          )}
        </div>
        <div className={css.columnsAndExport}>
          <div className={css.clearSelected}>
            {selectedNotices && selectedNotices.length > 0 && (
              <ClearSelectedButton
                count={selectedNotices.length}
                onClear={() => setSelectedNotices([])}
              />
            )}
          </div>
          <div className={css.editColumnsButton}>
            {selectedNotices && selectedNotices.length === 0 && (
              <EditColumnsButton
                selectedColumns={appliedColumns.map((c) => {
                  return {
                    field: c,
                    visible: true,
                  } as ColumnSetting<NoticeColumns>;
                })}
                onNewColumnSettingChange={(columns) => {
                  setNoticeTableColumns(columns.map((c) => c.field) as NoticeColumns[]);
                }}
                allAvailableColumns={ALL_NOTICE_COLUMNS}
                dataType={EventDataTypes.record}
              />
            )}
          </div>
          <ExportNoticesButton filters={appliedFilters} selectedNotices={selectedNotices} />
        </div>
      </div>
      {!simple && (
        <div className={css.viewFilters}>
          <QuickFilterBar
            filters={appliedFilters}
            onClearFilter={(key) =>
              setNoticeTableFilters({
                ...appliedFilters,
                [key]: Array.isArray(defaultFilters[key]) ? [] : undefined,
              })
            }
            hiddenSections={hiddenSections}
            hiddenFields={hiddenFilters}
            onFilterClick={(key) => {
              setIsFilterDrawerOpen(true);
              setActiveGroups([findKeybyValue(key, groupedKeys) || ""]);
            }}
            clearAllFilters={() => setNoticeTableFilters(defaultFilters)}
          />
        </div>
      )}

      <NoticeTable
        filters={appliedFilters}
        columnSettings={appliedColumns}
        onSortChange={(sortOrder: SortState) =>
          setNoticeTableFilters({ ...appliedFilters, sort: sortOrder })
        }
        selectedRows={selectedNotices}
        onSelectedRowsChange={(rows) => setSelectedNotices(rows)}
      />

      {!simple && (
        <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
          {isFilterDrawerOpen && (
            <FilterForm
              onChange={debouncedSetNoticeTableFilters}
              filters={appliedFilters}
              defaultFilters={defaultFilters}
              options={{
                disabledSections: hiddenSections ? hiddenSections : undefined,
              }}
              onClose={() => setIsFilterDrawerOpen(false)}
              activeGroups={activeGroups}
              onActiveGroupsChange={setActiveGroups}
              location={location}
            />
          )}
        </FilterDrawer>
      )}
    </>
  );
}
